import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

// Auth context - needed immediately
import { AuthProvider, useAuth } from './contexts/AuthContext'

// Landing page loads immediately (critical path)
import LandingPage from './website/pages/LandingPage'

// Visitor questionnaire gate (client-only overlay; never renders during SSR)
import QuestionnaireGate from './components/QuestionnaireGate'
import ExitIntentCapture from './website/components/ExitIntentCapture'
import { WEB_APP_URL } from './config/webApp'

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-void text-terminal-text">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zypheron-500 mx-auto mb-4"></div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
)

// Lazy load all other pages
const ProductOverview = lazy(() => import('./website/pages/ProductOverview'))
const CybersecurityIDE = lazy(() => import('./website/pages/CybersecurityIDE'))
const WhyCobraAI = lazy(() => import('./website/pages/WhyCobraAI'))
const AboutUs = lazy(() => import('./website/pages/AboutUs'))
const Team = lazy(() => import('./website/pages/Team'))
const Download = lazy(() => import('./website/pages/Download'))
const Changelog = lazy(() => import('./website/pages/Changelog'))
const CLI = lazy(() => import('./website/pages/CLI'))
const ZypheronCloud = lazy(() => import('./website/pages/ZypheronCloud'))
const ErebusC2 = lazy(() => import('./website/pages/ErebusC2'))
const FAQ = lazy(() => import('./website/pages/FAQ'))
const Documentation = lazy(() => import('./website/pages/Documentation'))
const TerminalPage = lazy(() => import('./website/pages/TerminalPage'))
const Enterprise = lazy(() => import('./website/pages/Enterprise'))
const Login = lazy(() => import('./website/pages/Login'))
const Signup = lazy(() => import('./website/pages/Signup'))
const Subscribe = lazy(() => import('./website/pages/Subscribe'))
const BillingIde = lazy(() => import('./website/pages/billing/BillingIde'))
const BillingCloud = lazy(() => import('./website/pages/billing/BillingCloud'))
const BillingPrivateSystem = lazy(() => import('./website/pages/billing/BillingPrivateSystem'))
const Qualify = lazy(() => import('./website/pages/Qualify'))
const Playbook = lazy(() => import('./website/pages/Playbook'))
const AuthConfirmed = lazy(() => import('./website/pages/AuthConfirmed'))
const AdminLeads = lazy(() => import('./website/pages/AdminLeads'))
const AdminHub = lazy(() => import('./website/pages/AdminHub'))
const AdminUsers = lazy(() => import('./website/pages/AdminUsers'))
const AdminFreeAccess = lazy(() => import('./website/pages/AdminFreeAccess'))
const AdminStats = lazy(() => import('./website/pages/AdminStats'))
// /desktop/login is preserved as a back-compat alias; it redirects to /login
// (which now hosts the unified PKCE handshake + standard sign-in). Older
// desktop builds in the wild still open /desktop/login.
const DesktopLoginRedirect: React.FC = () => {
  const location = useLocation()
  return <Navigate to={`/login${location.search}${location.hash}`} replace />
}
// /billing used to be a single 3-card page keyed by ?plan=. It's now split
// into one page per tier; route legacy links there by plan, preserving the
// query string (checkout success/cancelled/xmr_order params ride on it).
const BillingLegacyRedirect: React.FC = () => {
  const location = useLocation()
  const plan = new URLSearchParams(location.search).get('plan')
  const target = plan === 'pro' ? '/billing/cloud' : plan === 'enterprise' ? '/billing/private-system' : '/billing/ide'
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}
// The Private System (high-ticket) tier is gated behind the /qualify form.
// Until this browser has passed the qualifier, bounce to it.
const PrivateSystemGate: React.FC = () => {
  let passed = false
  try {
    passed = Boolean(localStorage.getItem('zy:qualify-passed'))
  } catch {
    passed = false
  }
  return passed ? <BillingPrivateSystem /> : <Navigate to="/qualify" replace />
}
const InstallGuide = lazy(() => import('./website/pages/docs/InstallGuide'))
const FirstRunGuide = lazy(() => import('./website/pages/docs/FirstRunGuide'))
const AIAndMCPGuide = lazy(() => import('./website/pages/docs/AIAndMCPGuide'))
const Quickstart = lazy(() => import('./website/pages/docs/Quickstart'))
const WhyZypheronDesktop = lazy(() => import('./website/pages/docs/WhyZypheronDesktop'))
const PlaybookADCloudRecon = lazy(() => import('./website/pages/docs/PlaybookADCloudRecon'))
const PlaybookRETriage = lazy(() => import('./website/pages/docs/PlaybookRETriage'))
const PlaybookWebRecon = lazy(() => import('./website/pages/docs/PlaybookWebRecon'))
const PlaybookReporting = lazy(() => import('./website/pages/docs/PlaybookReporting'))


// Location Pages - Countries
const USA = lazy(() => import('./website/pages/locations/USA'))
const UK = lazy(() => import('./website/pages/locations/UK'))
const UAE = lazy(() => import('./website/pages/locations/UAE'))
const Canada = lazy(() => import('./website/pages/locations/Canada'))
const Australia = lazy(() => import('./website/pages/locations/Australia'))
const Germany = lazy(() => import('./website/pages/locations/Germany'))
const France = lazy(() => import('./website/pages/locations/France'))
const Singapore = lazy(() => import('./website/pages/locations/Singapore'))
const Brazil = lazy(() => import('./website/pages/locations/Brazil'))

// Location Pages - Cities
const Houston = lazy(() => import('./website/pages/locations/Houston'))
const NewYork = lazy(() => import('./website/pages/locations/NewYork'))
const SanFrancisco = lazy(() => import('./website/pages/locations/SanFrancisco'))
const London = lazy(() => import('./website/pages/locations/London'))
const Toronto = lazy(() => import('./website/pages/locations/Toronto'))
const Sydney = lazy(() => import('./website/pages/locations/Sydney'))

// Blog & Resources
const Blog = lazy(() => import('./website/pages/Blog'))
const AIPentestingWorkflow = lazy(() => import('./website/pages/blog/AIPentestingWorkflow'))
const BugBountyRecon = lazy(() => import('./website/pages/blog/BugBountyRecon'))
const JuniorToSenior = lazy(() => import('./website/pages/blog/JuniorToSenior'))
const NmapAITutorial = lazy(() => import('./website/pages/blog/NmapAITutorial'))
const OpenSourceSecurityTools = lazy(() => import('./website/pages/blog/OpenSourceSecurityTools'))
const MCPIntegrationGuide = lazy(() => import('./website/pages/blog/MCPIntegrationGuide'))
const DeathOfFlagMemorization = lazy(() => import('./website/pages/blog/DeathOfFlagMemorization'))
const HybridArchitecture = lazy(() => import('./website/pages/blog/HybridArchitecture'))
const AIAgentsInSecurity = lazy(() => import('./website/pages/blog/AIAgentsInSecurity'))
const FutureOfAutonomousPentesting = lazy(() => import('./website/pages/blog/FutureOfAutonomousPentesting'))
const AIAssistedBugBounty = lazy(() => import('./website/pages/blog/AIAssistedBugBounty'))
const AISecurityTools2026 = lazy(() => import('./website/pages/blog/AISecurityTools2026'))
const NucleiAIAutomation = lazy(() => import('./website/pages/blog/NucleiAIAutomation'))
const AINativeC2Design = lazy(() => import('./website/pages/blog/AINativeC2Design'))
const SourceBootstrapVsReleaseInstaller = lazy(() => import('./website/pages/blog/SourceBootstrapVsReleaseInstaller'))
const ZypheronDoctorGuide = lazy(() => import('./website/pages/blog/ZypheronDoctorGuide'))
const OllamaLocalAIGuide = lazy(() => import('./website/pages/blog/OllamaLocalAIGuide'))
const TheCybersecurityIDE = lazy(() => import('./website/pages/blog/TheCybersecurityIDE'))
const ADCloudAttackPaths = lazy(() => import('./website/pages/blog/ADCloudAttackPaths'))
const ReverseEngineeringLocalLLM = lazy(() => import('./website/pages/blog/ReverseEngineeringLocalLLM'))
const ZypheronDesktopLinuxGA = lazy(() => import('./website/pages/blog/ZypheronDesktopLinuxGA'))
const First30Minutes = lazy(() => import('./website/pages/blog/First30Minutes'))
const ReconToReport = lazy(() => import('./website/pages/blog/ReconToReport'))
const FragmentedStackVsWorkspace = lazy(() => import('./website/pages/blog/FragmentedStackVsWorkspace'))
const InternalTeamsStandardize = lazy(() => import('./website/pages/blog/InternalTeamsStandardize'))
const InstallAndForgetFails = lazy(() => import('./website/pages/blog/InstallAndForgetFails'))
const ComplianceDashboardFrameworks = lazy(() => import('./website/pages/blog/ComplianceDashboardFrameworks'))
const PentestReportTemplates = lazy(() => import('./website/pages/blog/PentestReportTemplates'))
const CliDesktopPairing = lazy(() => import('./website/pages/blog/CliDesktopPairing'))
const AutomatePentestReporting = lazy(() => import('./website/pages/blog/AutomatePentestReporting'))
const ComplianceEvidenceFromScans = lazy(() => import('./website/pages/blog/ComplianceEvidenceFromScans'))
const LocalFirstPentestData = lazy(() => import('./website/pages/blog/LocalFirstPentestData'))
const FiveToolTax = lazy(() => import('./website/pages/blog/FiveToolTax'))
const BoardInternalSecurityAssessment = lazy(() => import('./website/pages/blog/BoardInternalSecurityAssessment'))
const FirstInternalADAssessment = lazy(() => import('./website/pages/blog/FirstInternalADAssessment'))
const EvidenceRecoveryPentestReports = lazy(() => import('./website/pages/blog/EvidenceRecoveryPentestReports'))
const SecurityDataShouldNotDefaultSaas = lazy(() => import('./website/pages/blog/SecurityDataShouldNotDefaultSaas'))
const CopilotVsAutonomousSecurityAgent = lazy(() => import('./website/pages/blog/CopilotVsAutonomousSecurityAgent'))
const QuarterlySecurityAssessmentChecklist = lazy(() => import('./website/pages/blog/QuarterlySecurityAssessmentChecklist'))
const ClientReadyPentestDeliverables = lazy(() => import('./website/pages/blog/ClientReadyPentestDeliverables'))
const ErebusIsOut = lazy(() => import('./website/pages/blog/ErebusIsOut'))
const EndOfMSPs = lazy(() => import('./website/pages/blog/EndOfMSPs'))
const ZypheronV141 = lazy(() => import('./website/pages/blog/ZypheronV141'))
const ZypheronV15 = lazy(() => import('./website/pages/blog/ZypheronV15'))
const Erebus11 = lazy(() => import('./website/pages/blog/Erebus11'))
const ErebusWinrmPthSeal = lazy(() => import('./website/pages/blog/ErebusWinrmPthSeal'))
const WhatIsErebusC2 = lazy(() => import('./website/pages/blog/WhatIsErebusC2'))
const ErebusTwoPersonRedTeam = lazy(() => import('./website/pages/blog/ErebusTwoPersonRedTeam'))
const ZypheronErebusOneStack = lazy(() => import('./website/pages/blog/ZypheronErebusOneStack'))
const WhyErebusIsOpenSource = lazy(() => import('./website/pages/blog/WhyErebusIsOpenSource'))
const HowErebusC2Works = lazy(() => import('./website/pages/blog/HowErebusC2Works'))
const ErebusArchitecture = lazy(() => import('./website/pages/blog/ErebusArchitecture'))
const ErebusAINativeDesign = lazy(() => import('./website/pages/blog/ErebusAINativeDesign'))
const ErebusGettingCloser = lazy(() => import('./website/pages/blog/ErebusGettingCloser'))
const ErebusVsMetasploit = lazy(() => import('./website/pages/blog/ErebusVsMetasploit'))
const ErebusVsSliver = lazy(() => import('./website/pages/blog/ErebusVsSliver'))
const ErebusVsHavoc = lazy(() => import('./website/pages/blog/ErebusVsHavoc'))
const ErebusVsCobaltStrike = lazy(() => import('./website/pages/blog/ErebusVsCobaltStrike'))
const ZypheronVsVsCode = lazy(() => import('./website/pages/blog/ZypheronVsVsCode'))
const ZypheronVsTmuxVim = lazy(() => import('./website/pages/blog/ZypheronVsTmuxVim'))
const ZypheronVsManticoreAi = lazy(() => import('./website/pages/blog/ZypheronVsManticoreAi'))
const ZypheronVsOpenvas = lazy(() => import('./website/pages/blog/ZypheronVsOpenvas'))
const ZypheronVsNessus = lazy(() => import('./website/pages/blog/ZypheronVsNessus'))
const ZypheronVsBloodhound = lazy(() => import('./website/pages/blog/ZypheronVsBloodhound'))
const ZypheronVsPlextrac = lazy(() => import('./website/pages/blog/ZypheronVsPlextrac'))
const ZypheronVsBurpSuite = lazy(() => import('./website/pages/blog/ZypheronVsBurpSuite'))
const WhatIsC2Infrastructure = lazy(() => import('./website/pages/blog/WhatIsC2Infrastructure'))
const WhatIsAttackPathMapping = lazy(() => import('./website/pages/blog/WhatIsAttackPathMapping'))
const WhatIsPurpleTeaming = lazy(() => import('./website/pages/blog/WhatIsPurpleTeaming'))
const BestC2Frameworks2026 = lazy(() => import('./website/pages/blog/BestC2Frameworks2026'))
const BestPentestReportingTools2026 = lazy(() => import('./website/pages/blog/BestPentestReportingTools2026'))
const StateOfInternalPentestTooling2026 = lazy(() => import('./website/pages/blog/StateOfInternalPentestTooling2026'))
const ComparisonArticleRoute = lazy(() => import('./website/pages/blog/ComparisonArticleRoute'))
const EngagementArticleRoute = lazy(() => import('./website/pages/blog/EngagementArticleRoute'))

// Tool Integration Pages
const NmapIntegration = lazy(() => import('./website/pages/tools/NmapIntegration'))
const MetasploitIntegration = lazy(() => import('./website/pages/tools/MetasploitIntegration'))
const HydraIntegration = lazy(() => import('./website/pages/tools/HydraIntegration'))

const Resources = lazy(() => import('./website/pages/Resources'))
const CaseStudies = lazy(() => import('./website/pages/CaseStudies'))
const Guides = lazy(() => import('./website/pages/Guides'))
const Contact = lazy(() => import('./website/pages/Contact'))

// Solution Pages
const PentestFirms = lazy(() => import('./website/pages/solutions/PentestFirms'))
const InternalSecurityTeams = lazy(() => import('./website/pages/solutions/InternalSecurityTeams'))

// Industry Pages
const Healthcare = lazy(() => import('./website/pages/industries/Healthcare'))
const Finance = lazy(() => import('./website/pages/industries/Finance'))
const Retail = lazy(() => import('./website/pages/industries/Retail'))
const Startups = lazy(() => import('./website/pages/industries/Startups'))


// Legal Pages
const TermsOfServicePage = lazy(() => import('./website/pages/TermsOfService'))
const EULAPage = lazy(() => import('./website/pages/EULA'))
const PrivacyPolicyPage = lazy(() => import('./website/pages/PrivacyPolicy'))

// Dashboard
const DashboardApp = lazy(() => import('./components/DashboardApp'))

const isProductionMarketingHost = () =>
  typeof window !== 'undefined' && window.location.hostname === 'zypheron.net'

// External redirect component for authenticated users
const ExternalRedirect = () => {
  React.useEffect(() => {
    window.location.href = WEB_APP_URL
  }, [])

  return (
    <div className="flex items-center justify-center min-h-screen bg-void text-terminal-text" role="main">
      <section className="text-center" aria-live="polite" aria-label="Redirecting to web app">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-cobra-500 mx-auto mb-4"
          role="status"
          aria-label="Loading spinner"
        ></div>
        <p className="text-terminal-muted text-base" role="status">Redirecting to ZYPHERON Web App...</p>
      </section>
    </div>
  )
}

// Simplified routing component for marketing site
const AppRoutes = () => {
  const { loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-void text-terminal-text" role="main">
        <section className="text-center" aria-live="polite" aria-label="Loading application">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-cobra-500 mx-auto mb-4"
            role="status"
            aria-label="Loading spinner"
          ></div>
          <p className="text-terminal-muted text-base" role="status">Loading ZYPHERON...</p>
        </section>
      </div>
    )
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing page - loads immediately */}
        <Route path="/" element={<LandingPage />} />

        {/* Website Routes - Public Marketing Pages */}
        <Route path="/product" element={<ProductOverview />} />
        <Route path="/cybersecurity-ide" element={<CybersecurityIDE />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/team" element={<Team />} />
        <Route path="/why-zypheron" element={<WhyCobraAI />} />
        <Route path="/download" element={<Download />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/releases" element={<Navigate to="/changelog" replace />} />
        <Route path="/waitlist" element={<Navigate to="/download" replace />} />
        <Route path="/trial" element={<Navigate to="/download" replace />} />
        <Route path="/cli" element={<CLI />} />
        <Route path="/cloud" element={<ZypheronCloud />} />
        <Route path="/ark-c2" element={<ErebusC2 />} />
        <Route path="/erebus-c2" element={<Navigate to="/ark-c2" replace />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/enterprise" element={<Enterprise />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/documentation/install" element={<InstallGuide />} />
        <Route path="/documentation/first-run" element={<FirstRunGuide />} />
        <Route path="/documentation/ai-and-mcp" element={<AIAndMCPGuide />} />
        <Route path="/documentation/quickstart" element={<Quickstart />} />
        <Route path="/documentation/why-zypheron-desktop" element={<WhyZypheronDesktop />} />
        <Route path="/documentation/playbook-ad-cloud-recon" element={<Navigate to="/blog/ad-cloud-recon-playbook" replace />} />
        <Route path="/documentation/playbook-reverse-engineering-triage" element={<PlaybookRETriage />} />
        <Route path="/documentation/playbook-web-recon" element={<PlaybookWebRecon />} />
        <Route path="/documentation/playbook-notes-findings-reporting" element={<PlaybookReporting />} />
        <Route path="/terminal" element={<Navigate to="/cli" replace />} />
        <Route path="/desktop" element={<Navigate to="/download" replace />} />


        {/* Blog & Resources */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/ai-pentesting-workflow" element={<AIPentestingWorkflow />} />
        <Route path="/blog/bug-bounty-recon-2025" element={<BugBountyRecon />} />
        <Route path="/blog/junior-to-senior-pentester" element={<JuniorToSenior />} />
        <Route path="/blog/nmap-ai-tutorial" element={<NmapAITutorial />} />
        <Route path="/blog/open-source-security-tools" element={<OpenSourceSecurityTools />} />
        <Route path="/blog/mcp-integration-guide" element={<MCPIntegrationGuide />} />
        <Route path="/blog/death-of-flag-memorization" element={<DeathOfFlagMemorization />} />
        <Route path="/blog/hybrid-architecture" element={<HybridArchitecture />} />
        <Route path="/blog/ai-agents-in-security" element={<AIAgentsInSecurity />} />
        <Route path="/blog/future-of-autonomous-pentesting" element={<FutureOfAutonomousPentesting />} />
        <Route path="/blog/ai-assisted-bug-bounty" element={<AIAssistedBugBounty />} />
        <Route path="/blog/ai-security-tools-2026" element={<AISecurityTools2026 />} />
        <Route path="/blog/nuclei-ai-automation" element={<NucleiAIAutomation />} />
        <Route path="/blog/ai-native-c2-design" element={<AINativeC2Design />} />
        <Route path="/blog/source-bootstrap-vs-release-installer" element={<SourceBootstrapVsReleaseInstaller />} />
        <Route path="/blog/zypheron-doctor-guide" element={<ZypheronDoctorGuide />} />
        <Route path="/blog/ollama-local-ai-guide" element={<OllamaLocalAIGuide />} />
        <Route path="/blog/the-cybersecurity-ide" element={<TheCybersecurityIDE />} />
        <Route path="/blog/ad-cloud-attack-paths" element={<ADCloudAttackPaths />} />
        <Route path="/blog/ad-cloud-recon-playbook" element={<PlaybookADCloudRecon />} />
        <Route path="/blog/zypheron-methodology" element={<Playbook />} />
        <Route path="/blog/reverse-engineering-local-llm" element={<ReverseEngineeringLocalLLM />} />
        <Route path="/blog/zypheron-desktop-linux-ga" element={<ZypheronDesktopLinuxGA />} />
        <Route path="/blog/first-30-minutes" element={<First30Minutes />} />
        <Route path="/blog/recon-to-notes-to-report" element={<ReconToReport />} />
        <Route path="/blog/fragmented-stack-vs-workspace" element={<FragmentedStackVsWorkspace />} />
        <Route path="/blog/internal-teams-standardize-workflows" element={<InternalTeamsStandardize />} />
        <Route path="/blog/install-and-forget-fails" element={<InstallAndForgetFails />} />
        <Route path="/blog/compliance-dashboard-frameworks" element={<ComplianceDashboardFrameworks />} />
        <Route path="/blog/pentest-report-templates" element={<PentestReportTemplates />} />
        <Route path="/blog/cli-desktop-pairing" element={<CliDesktopPairing />} />
        <Route path="/blog/automate-pentest-reporting" element={<AutomatePentestReporting />} />
        <Route path="/blog/compliance-evidence-from-scans" element={<ComplianceEvidenceFromScans />} />
        <Route path="/blog/local-first-pentest-data" element={<LocalFirstPentestData />} />
        <Route path="/blog/five-tool-tax-pentest-engagements" element={<FiveToolTax />} />
        <Route path="/blog/board-ready-internal-security-assessment" element={<BoardInternalSecurityAssessment />} />
        <Route path="/blog/first-internal-ad-assessment" element={<FirstInternalADAssessment />} />
        <Route path="/blog/pentest-report-writing-evidence-recovery" element={<EvidenceRecoveryPentestReports />} />
        <Route path="/blog/security-data-should-not-default-to-saas" element={<SecurityDataShouldNotDefaultSaas />} />
        <Route path="/blog/copilot-vs-autonomous-security-agent" element={<CopilotVsAutonomousSecurityAgent />} />
        <Route path="/blog/quarterly-security-assessment-checklist" element={<QuarterlySecurityAssessmentChecklist />} />
        <Route path="/blog/client-ready-pentest-deliverables" element={<ClientReadyPentestDeliverables />} />
        <Route path="/blog/erebus-is-out" element={<ErebusIsOut />} />
        <Route path="/blog/zypheron-v1-4-1-update" element={<ZypheronV141 />} />
        <Route path="/blog/zypheron-v1-5-update" element={<ZypheronV15 />} />
        <Route path="/blog/erebus-1-1" element={<Erebus11 />} />
        <Route path="/blog/erebus-winrm-pth-message-encryption" element={<ErebusWinrmPthSeal />} />
        <Route path="/blog/what-is-erebus-c2" element={<WhatIsErebusC2 />} />
        <Route path="/blog/erebus-two-person-red-team" element={<ErebusTwoPersonRedTeam />} />
        <Route path="/blog/zypheron-erebus-one-stack" element={<ZypheronErebusOneStack />} />
        <Route path="/blog/why-erebus-is-open-source" element={<WhyErebusIsOpenSource />} />
        <Route path="/blog/how-erebus-c2-works" element={<HowErebusC2Works />} />
        <Route path="/blog/erebus-architecture" element={<ErebusArchitecture />} />
        <Route path="/blog/erebus-ai-native-design" element={<ErebusAINativeDesign />} />
        <Route path="/blog/erebus-getting-closer" element={<ErebusGettingCloser />} />
        <Route path="/blog/erebus-vs-metasploit" element={<ErebusVsMetasploit />} />
        <Route path="/blog/erebus-vs-sliver" element={<ErebusVsSliver />} />
        <Route path="/blog/erebus-vs-havoc" element={<ErebusVsHavoc />} />
        <Route path="/blog/erebus-vs-cobalt-strike" element={<ErebusVsCobaltStrike />} />
        <Route path="/blog/zypheron-vs-vs-code" element={<ZypheronVsVsCode />} />
        <Route path="/blog/zypheron-vs-tmux-vim" element={<ZypheronVsTmuxVim />} />
        <Route path="/blog/zypheron-vs-burp-ai" element={<Navigate to="/blog/zypheron-vs-burp-suite" replace />} />
        <Route path="/blog/zypheron-vs-horizon3" element={<Navigate to="/blog/zypheron-vs-burp-suite" replace />} />
        <Route path="/blog/zypheron-vs-nodezero" element={<Navigate to="/blog/zypheron-vs-burp-suite" replace />} />
        <Route path="/blog/zypheron-vs-manticore-ai" element={<ZypheronVsManticoreAi />} />
        <Route path="/blog/zypheron-vs-openvas" element={<ZypheronVsOpenvas />} />
        <Route path="/blog/zypheron-vs-nessus" element={<ZypheronVsNessus />} />
        <Route path="/blog/zypheron-vs-bloodhound" element={<ZypheronVsBloodhound />} />
        <Route path="/blog/zypheron-vs-plextrac" element={<ZypheronVsPlextrac />} />
        <Route path="/blog/zypheron-vs-burp-suite" element={<ZypheronVsBurpSuite />} />
        <Route path="/blog/what-is-c2-infrastructure" element={<WhatIsC2Infrastructure />} />
        <Route path="/blog/what-is-attack-path-mapping" element={<WhatIsAttackPathMapping />} />
        <Route path="/blog/what-is-purple-teaming" element={<WhatIsPurpleTeaming />} />
        <Route path="/blog/best-c2-frameworks-2026" element={<BestC2Frameworks2026 />} />
        <Route path="/blog/best-pentest-reporting-tools-2026" element={<BestPentestReportingTools2026 />} />
        <Route path="/blog/state-of-internal-pentest-tooling-2026" element={<StateOfInternalPentestTooling2026 />} />
        <Route path="/blog/zypheron-vs-dradis" element={<ComparisonArticleRoute slug="zypheron-vs-dradis" />} />
        <Route path="/blog/zypheron-vs-faraday" element={<ComparisonArticleRoute slug="zypheron-vs-faraday" />} />
        <Route path="/blog/erebus-vs-brute-ratel" element={<ComparisonArticleRoute slug="erebus-vs-brute-ratel" />} />
        <Route path="/blog/erebus-vs-mythic" element={<ComparisonArticleRoute slug="erebus-vs-mythic" />} />
        <Route path="/blog/best-active-directory-security-tools-2026" element={<EngagementArticleRoute slug="best-active-directory-security-tools-2026" />} />
        <Route path="/blog/pentest-workflow-leaking-time-before-reporting" element={<EngagementArticleRoute slug="pentest-workflow-leaking-time-before-reporting" />} />
        <Route path="/blog/real-risk-finding-you-cannot-prove-later" element={<EngagementArticleRoute slug="real-risk-finding-you-cannot-prove-later" />} />
        <Route path="/blog/why-small-security-teams-need-repeatable-assessments" element={<EngagementArticleRoute slug="why-small-security-teams-need-repeatable-assessments" />} />
        <Route path="/blog/best-pentester-should-not-be-whole-process" element={<EngagementArticleRoute slug="best-pentester-should-not-be-whole-process" />} />
        <Route path="/blog/what-a-zypheron-workflow-review-covers" element={<EngagementArticleRoute slug="what-a-zypheron-workflow-review-covers" />} />
        <Route path="/blog/from-scan-output-to-client-ready-finding-in-zypheron" element={<EngagementArticleRoute slug="from-scan-output-to-client-ready-finding-in-zypheron" />} />
        <Route path="/blog/how-zypheron-keeps-operators-in-control" element={<EngagementArticleRoute slug="how-zypheron-keeps-operators-in-control" />} />
        <Route path="/blog/how-to-know-if-zypheron-fits-your-team" element={<EngagementArticleRoute slug="how-to-know-if-zypheron-fits-your-team" />} />
        <Route path="/blog/best-attack-path-management-tools-2026" element={<EngagementArticleRoute slug="best-attack-path-management-tools-2026" />} />
        <Route path="/blog/best-bloodhound-alternatives" element={<EngagementArticleRoute slug="best-bloodhound-alternatives" />} />
        <Route path="/blog/what-is-active-directory-security-assessment" element={<EngagementArticleRoute slug="what-is-active-directory-security-assessment" />} />
        <Route path="/blog/what-is-identity-threat-detection-response" element={<EngagementArticleRoute slug="what-is-identity-threat-detection-response" />} />
        <Route path="/blog/what-is-attack-path-management" element={<EngagementArticleRoute slug="what-is-attack-path-management" />} />
        <Route path="/blog/turn-nmap-nessus-bloodhound-output-into-report" element={<EngagementArticleRoute slug="turn-nmap-nessus-bloodhound-output-into-report" />} />
        <Route path="/blog/prioritize-active-directory-findings" element={<EngagementArticleRoute slug="prioritize-active-directory-findings" />} />
        <Route path="/blog/best-internal-pentest-tools-2026" element={<EngagementArticleRoute slug="best-internal-pentest-tools-2026" />} />
        <Route path="/blog/best-pentest-tools-small-security-teams" element={<EngagementArticleRoute slug="best-pentest-tools-small-security-teams" />} />
        <Route path="/blog/best-dradis-alternatives" element={<EngagementArticleRoute slug="best-dradis-alternatives" />} />
        <Route path="/blog/best-faraday-alternatives" element={<EngagementArticleRoute slug="best-faraday-alternatives" />} />
        <Route path="/blog/what-is-assumed-breach-assessment" element={<EngagementArticleRoute slug="what-is-assumed-breach-assessment" />} />
        <Route path="/blog/what-is-tier-zero-active-directory" element={<EngagementArticleRoute slug="what-is-tier-zero-active-directory" />} />
        <Route path="/blog/scope-internal-network-pentest" element={<EngagementArticleRoute slug="scope-internal-network-pentest" />} />
        <Route path="/blog/validate-remediation-after-pentest" element={<EngagementArticleRoute slug="validate-remediation-after-pentest" />} />
        <Route path="/blog/active-directory-assessment-checklist-50-500-person-companies" element={<EngagementArticleRoute slug="active-directory-assessment-checklist-50-500-person-companies" />} />
        <Route path="/blog/compare-ad-security-tools-bloodhound-pingcastle-purple-knight-zypheron" element={<EngagementArticleRoute slug="compare-ad-security-tools-bloodhound-pingcastle-purple-knight-zypheron" />} />
        <Route path="/blog/bloodhound-output-is-not-a-board-ready-report" element={<EngagementArticleRoute slug="bloodhound-output-is-not-a-board-ready-report" />} />
        <Route path="/blog/prioritize-tier-zero-findings-after-internal-ad-assessment" element={<EngagementArticleRoute slug="prioritize-tier-zero-findings-after-internal-ad-assessment" />} />
        <Route path="/blog/internal-pentest-report-for-leadership" element={<EngagementArticleRoute slug="internal-pentest-report-for-leadership" />} />
        <Route path="/blog/pentest-reporting-tools-vs-assessment-workspaces" element={<EngagementArticleRoute slug="pentest-reporting-tools-vs-assessment-workspaces" />} />
        <Route path="/blog/self-hosted-local-first-or-saas-security-teams-choose" element={<EngagementArticleRoute slug="self-hosted-local-first-or-saas-security-teams-choose" />} />
        <Route path="/blog/lean-security-teams-quarterly-internal-assessments" element={<EngagementArticleRoute slug="lean-security-teams-quarterly-internal-assessments" />} />
        <Route path="/blog/nessus-nmap-bloodhound-turning-tool-output-into-one-finding" element={<EngagementArticleRoute slug="nessus-nmap-bloodhound-turning-tool-output-into-one-finding" />} />
        <Route path="/blog/buy-pentest-tool-vs-hire-external-pentest" element={<EngagementArticleRoute slug="buy-pentest-tool-vs-hire-external-pentest" />} />
        <Route path="/blog/validate-remediation-without-starting-assessment-over" element={<EngagementArticleRoute slug="validate-remediation-without-starting-assessment-over" />} />
        <Route path="/blog/security-workflow-review-before-buying-tool" element={<EngagementArticleRoute slug="security-workflow-review-before-buying-tool" />} />
        <Route path="/blog/what-is-a-cybersecurity-ide" element={<EngagementArticleRoute slug="what-is-a-cybersecurity-ide" />} />
        <Route path="/blog/best-cybersecurity-ide-for-beginners" element={<EngagementArticleRoute slug="best-cybersecurity-ide-for-beginners" />} />
        <Route path="/blog/zypheron-for-bug-bounty-beginners" element={<EngagementArticleRoute slug="zypheron-for-bug-bounty-beginners" />} />
        <Route path="/blog/homelab-pentest-workspace" element={<EngagementArticleRoute slug="homelab-pentest-workspace" />} />
        <Route path="/blog/learn-pentesting-with-one-workspace" element={<EngagementArticleRoute slug="learn-pentesting-with-one-workspace" />} />
        <Route path="/blog/cybersecurity-notes-evidence-reports" element={<EngagementArticleRoute slug="cybersecurity-notes-evidence-reports" />} />
        <Route path="/blog/local-ai-cybersecurity-ide" element={<EngagementArticleRoute slug="local-ai-cybersecurity-ide" />} />
        <Route path="/blog/nmap-nuclei-burp-one-workspace" element={<EngagementArticleRoute slug="nmap-nuclei-burp-one-workspace" />} />
        <Route path="/blog/junior-pentester-workflow" element={<EngagementArticleRoute slug="junior-pentester-workflow" />} />
        <Route path="/blog/zypheron-vs-kali-terminal-workflow" element={<EngagementArticleRoute slug="zypheron-vs-kali-terminal-workflow" />} />
        <Route path="/blog/the-end-of-msps" element={<EndOfMSPs />} />
        <Route path="/blog/:slug" element={<Blog />} />

        {/* Tool Integration Pages */}
        <Route path="/tools/nmap" element={<NmapIntegration />} />
        <Route path="/tools/metasploit" element={<MetasploitIntegration />} />
        <Route path="/tools/burp-suite" element={<Navigate to="/blog/zypheron-vs-burp-suite" replace />} />
        <Route path="/tools/hydra" element={<HydraIntegration />} />

        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/case-studies" element={<CaseStudies />} />
        <Route path="/resources/guides" element={<Guides />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support" element={<Contact />} />

        {/* Solution Pages */}
        <Route path="/solutions/pentest-firms" element={<PentestFirms />} />
        <Route path="/solutions/internal-security-teams" element={<InternalSecurityTeams />} />

        {/* Industry Pages */}
        <Route path="/industries/healthcare" element={<Healthcare />} />
        <Route path="/industries/finance" element={<Finance />} />
        <Route path="/industries/retail" element={<Retail />} />
        <Route path="/industries/startups" element={<Startups />} />

        {/* GEO Landing Pages - Countries */}
        <Route path="/cybersecurity-usa" element={<USA />} />
        <Route path="/cybersecurity-uk" element={<UK />} />
        <Route path="/cybersecurity-uae" element={<UAE />} />
        <Route path="/cybersecurity-canada" element={<Canada />} />
        <Route path="/cybersecurity-australia" element={<Australia />} />
        <Route path="/cybersecurity-germany" element={<Germany />} />
        <Route path="/cybersecurity-france" element={<France />} />
        <Route path="/cybersecurity-singapore" element={<Singapore />} />
        <Route path="/cybersecurity-brazil" element={<Brazil />} />

        {/* GEO Landing Pages - Cities */}
        <Route path="/locations/houston" element={<Houston />} />
        <Route path="/locations/new-york" element={<NewYork />} />
        <Route path="/locations/san-francisco" element={<SanFrancisco />} />
        <Route path="/locations/london" element={<London />} />
        <Route path="/locations/toronto" element={<Toronto />} />
        <Route path="/locations/sydney" element={<Sydney />} />

        {/* Legacy/Compatibility Routes */}
        <Route path="/free-trial" element={<Navigate to="/download" replace />} />
        <Route path="/work-in-progress" element={<Navigate to="/" replace />} />

        {/* Dashboard routing */}
        <Route path="/dashboard" element={
          isProductionMarketingHost() ? <ExternalRedirect /> : <DashboardApp />
        } />
        <Route path="/engagements" element={
          isProductionMarketingHost() ? <ExternalRedirect /> : <DashboardApp />
        } />

        {/* Website Auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/email-list" element={<Navigate to="/subscribe" replace />} />
        <Route path="/newsletter" element={<Navigate to="/subscribe" replace />} />
        <Route path="/auth/confirmed" element={<AuthConfirmed />} />
        <Route path="/desktop/login" element={<DesktopLoginRedirect />} />

        {/* Hidden Admin Routes */}
        <Route path="/admin" element={<AdminHub />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/free-access" element={<AdminFreeAccess />} />
        <Route path="/admin/stats" element={<AdminStats />} />

        {/* Billing */}
        <Route path="/billing" element={<BillingLegacyRedirect />} />
        <Route path="/billing/ide" element={<BillingIde />} />
        <Route path="/billing/cloud" element={<BillingCloud />} />
        <Route path="/qualify" element={<Qualify />} />
        <Route path="/playbook" element={<Navigate to="/blog/zypheron-methodology" replace />} />
        <Route path="/billing/private-system" element={<PrivateSystemGate />} />
        <Route path="/billing/enterprise" element={<Navigate to="/billing/private-system" replace />} />
        <Route path="/light-billing" element={<ExternalRedirect />} />

        {/* Legacy subscription page */}
        <Route path="/free-trial-subscription" element={<Navigate to="/download" replace />} />

        {/* Legal Pages */}
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/terms-of-use" element={<TermsOfServicePage />} />
        <Route path="/tos" element={<TermsOfServicePage />} />
        <Route path="/eula" element={<EULAPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <AuthProvider>
      <QuestionnaireGate>
        <div className="min-h-screen bg-void text-terminal-text overflow-x-hidden w-full max-w-full">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-cobra-500 text-white px-4 py-2 rounded-lg z-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Skip to main content"
          >
            Skip to main content
          </a>

          <main id="main-content" role="main" aria-label="ZYPHERON Marketing Website" className="pb-16 lg:pb-0">
            <AppRoutes />
          </main>
          <ExitIntentCapture />
        </div>
      </QuestionnaireGate>
    </AuthProvider>
  )
}

export default App
